import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import * as actionTypes from '../constantes/actionTypes';
import { trackPlausibleEvent } from '../services/analytics';
import { StepView } from '../type';

const WristCamera = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const videoRef = useRef<HTMLVideoElement>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const [error, setError] = useState('');

	useEffect(() => {
		let isMounted = true;

		const startCamera = async () => {
			try {
				const stream = await navigator.mediaDevices.getUserMedia({
					video: {
						facingMode: { ideal: 'environment' },
						width: { ideal: 1280 },
						height: { ideal: 1280 },
					},
					audio: false,
				});

				if (!isMounted) {
					stream.getTracks().forEach((track) => track.stop());
					return;
				}

				streamRef.current = stream;
				if (videoRef.current) {
					videoRef.current.srcObject = stream;
					await videoRef.current.play();
				}
			} catch (cameraError) {
				if (isMounted) {
					setError('Camera unavailable');
					const errorName = cameraError instanceof DOMException ? cameraError.name : 'unknown';
					if (errorName === 'NotAllowedError' || errorName === 'SecurityError') {
						trackPlausibleEvent('sm_camera_permission_denied', {
							props: { camera: 'wrist' },
						});
					} else {
						trackPlausibleEvent('sm_photo_failed', {
							props: { camera: 'wrist', reason: errorName },
						});
					}
				}
			}
		};

		startCamera();

		return () => {
			isMounted = false;
			streamRef.current?.getTracks().forEach((track) => track.stop());
			streamRef.current = null;
		};
	}, []);

	const captureWrist = () => {
		const video = videoRef.current;

		if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
			return;
		}

		const canvas = document.createElement('canvas');
		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;

		const context = canvas.getContext('2d');
		if (!context) {
			return;
		}

		context.drawImage(video, 0, 0, canvas.width, canvas.height);
		trackPlausibleEvent('sm_wrist_photo_captured');
		dispatch({ type: actionTypes.ANALYSE_WRIST_PHOTO, value: canvas.toDataURL('image/jpeg', 0.92) });
		dispatch({ type: actionTypes.SWITCH_STEP, value: StepView.shadematchResults });
	};

	return (
		<Component>
			<Video ref={videoRef} autoPlay muted playsInline />
			<Instruction>{error || t('fill-camera', 'Fill camera with your wrist')}</Instruction>
			<GuideSquare />
			<ShutterButton type='button' onClick={captureWrist} aria-label='Take wrist photo'>
				<ShutterInner />
			</ShutterButton>
		</Component>
	);
};

const Component = styled.div`
	position: fixed;
	inset: 0;
	background: #000;
	overflow: hidden;
`;

const Video = styled.video`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
`;

const Instruction = styled.p`
	position: fixed;
	top: 84px;
	left: 24px;
	right: 24px;
	z-index: 2;
	margin: 0;
	color: white;
	text-align: center;
	font: normal normal 700 20px/120% 'Clinique Helvetica Neue';
	text-shadow: 0px 2px 4px rgba(0, 0, 0, 0.41);

	@media (max-width: 768px) {
		top: 64px;
		font-size: 18px;
	}
`;

const GuideSquare = styled.div`
	position: fixed;
	top: 50%;
	left: 50%;
	z-index: 2;
	width: min(62vw, 360px);
	aspect-ratio: 1 / 1;
	border: 3px solid rgba(255, 255, 255, 0.95);
	border-radius: 12px;
	box-shadow: 0 0 0 999px rgba(0, 0, 0, 0.12);
	transform: translate(-50%, -50%);
	pointer-events: none;
`;

const ShutterButton = styled.button`
	position: fixed;
	left: 50%;
	bottom: 44px;
	z-index: 3;
	width: 84px;
	height: 84px;
	padding: 5px;
	border: 4px solid white;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.12);
	transform: translateX(-50%);
	display: flex;
	align-items: center;
	justify-content: center;

	@media (max-width: 768px) {
		bottom: 34px;
		width: 76px;
		height: 76px;
	}
`;

const ShutterInner = styled.span`
	width: 100%;
	height: 100%;
	border-radius: 50%;
	background: white;
	display: block;
`;

export default WristCamera;
